import { create } from "zustand"
import { persist } from "zustand/middleware"
import type { ReportStatus, ReportCategory } from "@/types/reports"

interface Notification {
	id: string
	type: "success" | "error" | "warning" | "info"
	title: string
	message: string
	autoClose?: boolean
	duration?: number
	read?: boolean
}

interface Modal {
	deleteConfirm: boolean
	userForm: boolean
	templateForm: boolean
	reportExport: boolean
	settings: boolean
}

interface SearchFilters {
	statuses?: ReportStatus[]
	organizations?: string[]
	categories?: ReportCategory[]
	years?: number[]
}

interface UIState {
	sidebarOpen: boolean
	theme: "light" | "dark"
	notifications: Notification[]
	modals: Modal
	loading: {
		global: boolean
		reports: boolean
		templates: boolean
		users: boolean
	}
	searchFilters: SearchFilters
}

interface UIActions {
	// Sidebar
	toggleSidebar: () => void
	setSidebarOpen: (open: boolean) => void

	// Theme
	setTheme: (theme: "light" | "dark") => void
	toggleTheme: () => void

	// Notifications
	addNotification: (notification: Omit<Notification, "id">) => void
	removeNotification: (id: string) => void
	markNotificationAsRead: (id: string) => void
	clearNotifications: () => void

	// Modals
	openModal: (modal: keyof Modal) => void
	closeModal: (modal: keyof Modal) => void
	closeAllModals: () => void

	// Loading
	setLoading: (key: keyof UIState["loading"], loading: boolean) => void
	setGlobalLoading: (loading: boolean) => void

	// Search Filters
	setSearchFilters: (filters: SearchFilters) => void
	clearSearchFilters: () => void

	// Cleanup
	clearAllData: () => void
	clearUserData: () => void
	clearSessionData: () => void
}

type UIStore = UIState & UIActions

export const useUIStore = create<UIStore>()(
	persist(
		(set, get) => ({
			// Initial state
			sidebarOpen: true,
			theme: "light",
			notifications: [],
			modals: {
				deleteConfirm: false,
				userForm: false,
				templateForm: false,
				reportExport: false,
				settings: false,
			},
			loading: {
				global: false,
				reports: false,
				templates: false,
				users: false,
			},
			searchFilters: {},

			// Sidebar
			toggleSidebar: () => {
				set((state) => ({ sidebarOpen: !state.sidebarOpen }))
			},

			setSidebarOpen: (open) => {
				set({ sidebarOpen: open })
			},

			// Theme
			setTheme: (theme) => {
				set({ theme })
			},

			toggleTheme: () => {
				set((state) => ({
					theme: state.theme === "light" ? "dark" : "light",
				}))
			},

			// Notifications
			addNotification: (notification) => {
				const id = typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : Date.now().toString() + Math.random().toString(36).slice(2)
				const newNotification = {
					...notification,
					id,
					read: false,
				}

				set((state) => ({
					notifications: [...state.notifications, newNotification],
				}))

				// Auto-remove after duration if autoClose is true
				if (notification.autoClose !== false) {
					const duration = notification.duration || 5000
					setTimeout(() => {
						get().removeNotification(id)
					}, duration)
				}
			},

			removeNotification: (id) => {
				set((state) => ({
					notifications: state.notifications.filter((n) => n.id !== id),
				}))
			},

			markNotificationAsRead: (id) => {
				set((state) => ({
					notifications: state.notifications.map((n) => (n.id === id ? { ...n, read: true } : n)),
				}))
			},

			clearNotifications: () => {
				set({ notifications: [] })
			},

			// Modals
			openModal: (modal) => {
				set((state) => ({
					modals: { ...state.modals, [modal]: true },
				}))
			},

			closeModal: (modal) => {
				set((state) => ({
					modals: { ...state.modals, [modal]: false },
				}))
			},

			closeAllModals: () => {
				set({
					modals: {
						deleteConfirm: false,
						userForm: false,
						templateForm: false,
						reportExport: false,
						settings: false,
					},
				})
			},

			// Loading
			setLoading: (key, loading) => {
				set((state) => ({
					loading: { ...state.loading, [key]: loading },
				}))
			},

			setGlobalLoading: (loading) => {
				set((state) => ({
					loading: { ...state.loading, global: loading },
				}))
			},

			// Search Filters
			setSearchFilters: (filters) => {
				set({ searchFilters: filters })
			},

			clearSearchFilters: () => {
				set({ searchFilters: {} })
			},

			// Cleanup
			clearAllData: () => {
				set({
					sidebarOpen: true,
					theme: "light", // Reset theme
					notifications: [],
					modals: {
						deleteConfirm: false,
						userForm: false,
						templateForm: false,
						reportExport: false,
						settings: false,
					},
					loading: {
						global: false,
						reports: false,
						templates: false,
						users: false,
					},
					searchFilters: {},
				})
				// Clear persisted data from localStorage
				if (typeof window !== "undefined") {
					localStorage.removeItem("ui-store")
				}
			},

			clearUserData: () => {
				set(() => ({
					// Keep theme and sidebar preferences
					notifications: [],
					modals: {
						deleteConfirm: false,
						userForm: false,
						templateForm: false,
						reportExport: false,
						settings: false,
					},
					loading: {
						global: false,
						reports: false,
						templates: false,
						users: false,
					},
					searchFilters: {},
				}))
				// Don't clear localStorage - preserve theme and sidebar preferences
			},

			clearSessionData: () => {
				set(() => ({
					// Clear notifications and modals, keep everything else
					notifications: [],
					modals: {
						deleteConfirm: false,
						userForm: false,
						templateForm: false,
						reportExport: false,
						settings: false,
					},
				}))
			},
		}),
		{
			name: "ui-store",
			partialize: (state) => ({
				sidebarOpen: state.sidebarOpen,
				theme: state.theme,
			}),
		}
	)
)
